// ---------------------------------------------------------------------------
// History — the "folder" of finished work for one project.
// ---------------------------------------------------------------------------
// Shawn: "when you complete a project can it go into one folder so we can look
// back on those events later." Until now the only way was the board's Completed
// checkbox, which dumps every finished card back INTO the live columns.
//
// This is a read-only LENS over closed states — deliberately not a physical move
// to an archive stage/project, because any stage/project write on completion
// would re-enter the legacy Odoo `project_customization` automation.
//
// It fetches its own page and never touches `tasksStore`, so finished cards can
// never pollute the board's state or its socket reconciliation.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, ListTree, X } from 'lucide-react';
import type { ClosedTasksPage, TaskListItem } from '@cg-projects/shared';
import { api } from '@/core/api/apiClient';
import { Icon } from '@/core/ui/icons';
import { useTaskDetailStore } from '@/features/task-detail/store/taskDetailStore';
import { useAreaStore } from '@/features/areas/store/areaStore';
import { AssigneeStack, TagChip, Spinner } from '@/core/ui/atoms';
import { formatDate } from '@/core/ui/helpers';

const PAGE = 50;
type States = 'done' | 'cancelled' | 'both';

function monthLabel(iso: string | null | undefined): string {
  if (!iso) return 'Undated';
  // Group on the SAME calendar date the row renders (the raw date part), or a row
  // near a month boundary lands under a header that contradicts its own date.
  const [y, m] = iso.slice(0, 10).split('-');
  if (!y || !m) return 'Undated';
  const d = new Date(Number(y), Number(m) - 1, 1);
  return Number.isNaN(d.getTime()) ? 'Undated' : d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

export function HistoryView({ areaId }: { areaId: number }) {
  const open = useTaskDetailStore((s) => s.open);
  const area = useAreaStore((s) => s.areas.find((a) => a.id === areaId));
  const [items, setItems] = useState<TaskListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [states, setStates] = useState<States>('done');
  const [includeSubtasks, setIncludeSubtasks] = useState(false);
  const [error, setError] = useState(false);

  // Search is typed-into and the chips are switched, so responses can land out of
  // order. Only the newest request may write state.
  const reqSeq = useRef(0);
  const load = useCallback(async (reset: boolean, q: string, st: States, subs: boolean, offset: number) => {
    const seq = ++reqSeq.current;
    setLoading(true);
    try {
      const stateParam = st === 'both' ? 'done,cancelled' : st === 'cancelled' ? 'cancelled' : 'done';
      const page = await api.get<ClosedTasksPage>(
        `/areas/${areaId}/tasks/closed?states=${stateParam}&includeSubtasks=${subs}&limit=${PAGE}&offset=${offset}` +
        (q ? `&q=${encodeURIComponent(q)}` : ''),
      );
      if (seq !== reqSeq.current) return; // superseded
      setError(false);
      setTotal(page.total);
      setItems((prev) => (reset ? page.items : [...prev, ...page.items]));
    } catch {
      // A failed "Load more" must NOT wipe what's already on screen, and a failure
      // must never masquerade as "nothing finished yet".
      if (seq === reqSeq.current) {
        setError(true);
        if (reset) { setItems([]); setTotal(0); }
      }
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, [areaId]);

  useEffect(() => {
    const t = setTimeout(() => { void load(true, search, states, includeSubtasks, 0); }, 250);
    return () => clearTimeout(t);
  }, [search, states, includeSubtasks, load]);

  // Group by the month it closed — the way you actually look back on events.
  const groups: Array<{ label: string; rows: TaskListItem[] }> = [];
  for (const t of items) {
    const label = monthLabel((t as TaskListItem & { closedAt?: string | null }).closedAt);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.rows.push(t);
    else groups.push({ label, rows: [t] });
  }

  const chip = (active: boolean) =>
    `rounded px-2 py-0.5 text-[11px] font-medium transition ${active ? 'bg-active text-ink' : 'text-muted hover:text-ink'}`;

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2">
        <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-faint">
          History{total ? ` · ${total.toLocaleString()} finished` : ''}
        </span>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search finished work…"
          className="h-8 min-w-0 flex-1 rounded-md border border-line bg-canvas px-3 text-xs text-ink outline-none placeholder:text-faint focus:border-brand sm:max-w-[18rem]"
        />
        <nav className="flex shrink-0 items-center gap-0.5 rounded-md border border-line bg-canvas p-0.5">
          {([['done', 'Done'], ['cancelled', 'Cancelled'], ['both', 'Both']] as const).map(([v, label]) => (
            <button key={v} onClick={() => setStates(v)} className={chip(states === v)}>{label}</button>
          ))}
        </nav>
        <label className="flex shrink-0 cursor-pointer select-none items-center gap-1.5 rounded-md border border-line px-2 py-1 text-[11px] text-muted hover:bg-hover">
          <input type="checkbox" checked={includeSubtasks} onChange={(e) => setIncludeSubtasks(e.target.checked)} className="accent-brand" />
          Sub-tasks
        </label>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {error && items.length > 0 && (
          <div className="mb-2 rounded-md border border-danger/40 bg-danger/10 px-3 py-1.5 text-[11px] text-danger">
            Could not load more — check your connection and try again.
          </div>
        )}
        {error && items.length === 0 ? (
          <div className="pt-10 text-center text-sm text-danger">Could not load history. Try again.</div>
        ) : loading && items.length === 0 ? (
          <div className="flex h-full items-center justify-center"><Spinner label="Loading history…" /></div>
        ) : items.length === 0 ? (
          <div className="pt-10 text-center text-sm text-faint">
            {search ? 'Nothing finished matches that.' : 'Nothing finished yet — completed work collects here automatically.'}
          </div>
        ) : (
          <>
            {groups.map((g) => (
              <div key={g.label} className="mb-4">
                <div className="sticky top-0 z-10 -mx-1 mb-1.5 bg-page/95 px-1 py-1 text-[11px] font-semibold uppercase tracking-wide text-faint backdrop-blur">
                  {g.label} · {g.rows.length}
                </div>
                {g.rows.map((t) => {
                  const closedAt = (t as TaskListItem & { closedAt?: string | null }).closedAt;
                  const cancelled = t.state === '1_canceled';
                  return (
                    <button
                      key={t.id}
                      onClick={() => open(t.id)}
                      className="mb-1.5 flex w-full items-start gap-2 rounded-lg border border-line bg-canvas p-2.5 text-left transition hover:border-(--color-line-strong) hover:bg-hover"
                    >
                      <span className={`mt-0.5 shrink-0 ${cancelled ? 'text-faint' : 'text-brand'}`}><Icon i={cancelled ? X : Check} size={14} /></span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13px] leading-snug text-ink">{t.name}</span>
                        <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted">
                          {closedAt && <span title={`Finished ${formatDate(closedAt.slice(0, 10))}`}>{formatDate(closedAt.slice(0, 10))}</span>}
                          {cancelled && <span className="text-faint">cancelled</span>}
                          {t.childCount > 0 && <span className="flex items-center gap-0.5" title="Sub-tasks done"><Icon i={ListTree} size={14} /> {t.childDoneCount}/{t.childCount}</span>}
                          {(t.contentTags.length ? t.contentTags : t.tags).slice(0, 3).map((tg) => <TagChip key={`${tg.id}-${tg.name}`} tag={tg} />)}
                        </span>
                      </span>
                      <span className="shrink-0"><AssigneeStack assignees={t.assignees} /></span>
                    </button>
                  );
                })}
              </div>
            ))}
            {items.length < total && (
              <button
                onClick={() => void load(false, search, states, includeSubtasks, items.length)}
                disabled={loading}
                className="mx-auto mt-2 block rounded-md border border-line px-3 py-1.5 text-xs text-muted transition hover:bg-hover hover:text-ink disabled:opacity-40"
              >
                {loading ? 'Loading…' : `Load more (${(total - items.length).toLocaleString()} left)`}
              </button>
            )}
            <p className="mt-4 text-center text-[11px] text-faint">
              Finished {area?.name ? `${area.name} ` : ''}work, newest first. Open any card for its full record.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
